import {orange} from "@mui/material/colors";
import {
    AdjustCol,
    BoolCol,
    Column,
    DistanceCol,
    NominalWidth,
    NumberCol,
    RenderStringCol,
    StringCol,
    ViewOnly
} from "../../../../misc/scroller/Table";
import {InventoryRequest} from "../../../../api/Logistics";
import {CheckboxSelectorCol} from "../Checkbox";
import {PhaseViewCol} from "../openings/PhaseCol";
import {Checkbox2Col} from "../ShopTableCol";
import {DimensionCol} from "../extras/DimensionCol";
import {UnitSystem} from "../../../../api/Projects";
import {prepsAsString} from "../../../logistics/warehouse/InventoryFrameTable";
import {projectTablePrefName} from "../TablePrefName";
import {LogisticsView} from "./ViewButton";
import {LogisticsTab} from "./Logistics";

export const reviewStatusPendingColor = orange["200"];

// column prefs for the release view keep the names of the old release screen
// so saved widths and ordering carry over
export function releasePrefName(project: number, type: string) {
    switch(type) {
        case "frame-hm": return projectTablePrefName("project.release.frames", project);
        case "door-hm":
        case "door-wd": return projectTablePrefName("project.release.doors", project);
        case "hardware": return projectTablePrefName("project.release.hardware", project);
        case "frame-anchor": return projectTablePrefName("project.release.frameAnchors", project);
        case "div10": return projectTablePrefName("project.release.div10", project);
        default: return projectTablePrefName("project.release." + type, project);
    }
}

export function releaseListParams(view: LogisticsView, tab: LogisticsTab): {state?: string; kind?: string} {
    if(view !== "release") return {};

    return {
        state: "not-released",
        // anchors that are part of the frame get released with the frame
        kind: tab === "frame-anchor" ? "independent-frame-anchors" : undefined,
    }
}

export function releaseColumns(type: LogisticsTab, sys: UnitSystem): (Column<InventoryRequest>|null|false)[] | null {
    switch(type) {
        case "frame-hm":
            return [
                CheckboxSelectorCol(),
                ViewOnly(StringCol("Floor", "floor", 80)),
                PhaseViewCol<InventoryRequest>("phase", 80),
                ViewOnly(Checkbox2Col("Ext", "exterior", {disabled: (o) => true}) as any),
                AdjustCol(ViewOnly(StringCol("Opening", "openingName", 80)), {
                    fixed: true
                }),
                ViewOnly(StringCol("Location", "locationOne", 80)),
                ViewOnly(StringCol("To/From", "locationTransition", 40)),
                ViewOnly(StringCol("Location 2", "locationTwo", 80)),
                ViewOnly(NumberCol("Qty", "qty", 50)),
                ViewOnly(StringCol("Type", "openingType", 150)),
                ViewOnly(NominalWidth<InventoryRequest>("Nom. Width", sys, "nominalWidth", "inactiveDoorWidth")),
                ViewOnly(StringCol("Nom. Height", "nominalHeight")),
                ViewOnly(StringCol("Handing", "handing", 150)),
                ViewOnly(StringCol("Label", "frame.label", 150)),
                ViewOnly(StringCol("Series", "frame.series", 100)),
                ViewOnly(StringCol("Manufacturer", "manufacturerName", 120)),
                ViewOnly(StringCol("Material", "frame.material", 70)),
                ViewOnly(StringCol("Gauge", "frame.gauge", 50)),
                ViewOnly(StringCol("Screen Elev", "frame.screenElev", 100)),
                ViewOnly(StringCol("Profile", "frame.profile", 150)),
                ViewOnly(StringCol("Jamb", "frame.jambDepth", 80)),
                ViewOnly(StringCol("Construction", "frame.construction", 100)),
                ViewOnly(RenderStringCol<InventoryRequest>("HW. Preps", d => prepsAsString(d.frame?.preps), 150)),
                ViewOnly(StringCol("Anchor", "frameAnchorProductName", 150)),
            ]
        case "door-hm":
        case "door-wd":
            return [
                CheckboxSelectorCol(),
                ViewOnly(StringCol("Floor", "floor", 80)),
                PhaseViewCol<InventoryRequest>("phase", 80),
                ViewOnly(Checkbox2Col("Ext", "exterior", {disabled: (o) => true}) as any),
                AdjustCol(ViewOnly(StringCol("Opening", "openingName", 80)), {
                    fixed: true
                }),
                ViewOnly(StringCol("Location", "locationOne", 80)),
                ViewOnly(StringCol("To/From", "locationTransition", 40)),
                ViewOnly(StringCol("Location 2", "locationTwo", 80)),
                ViewOnly(StringCol("Type", "openingType", 150)),
                ViewOnly(NumberCol("Qty", "qty", 50)),
                ViewOnly(NominalWidth<InventoryRequest>("Nominal Width", sys, "nominalWidth", "inactiveDoorWidth")),
                ViewOnly(StringCol("Nom. Height", "nominalHeight", 150)),
                ViewOnly(StringCol("Elevation", "door.elevation", 150)),
                ViewOnly(StringCol("Handing", "handing", 150)),
                ViewOnly(StringCol("Label", "door.label", 150)),
                ViewOnly(StringCol("Series", "door.series", 150)),
                ViewOnly(StringCol("Manufacturer", "manufacturerName", 120)),
                ViewOnly(StringCol("Material", "door.material", 150)),
                ViewOnly(StringCol("Finish", "door.finish", 150)),
                ViewOnly(StringCol("Gauge", "door.gauge", 80)),
                ViewOnly(DistanceCol("Thickness", sys, "door.thickness", 100)),
                ViewOnly(StringCol("Core", "door.core", 70)),
                ViewOnly(StringCol("Seam", "door.seam", 70)),
            ]
        case "hardware":
            return [
                CheckboxSelectorCol(),
                ViewOnly(StringCol("Floor", "floor", 80)),
                PhaseViewCol<InventoryRequest>("phase", 80),
                AdjustCol(ViewOnly(StringCol("Opening", "openingName", 80)), {
                    fixed: true
                }),
                ViewOnly(Checkbox2Col("Ext", "exterior", {disabled: (o) => true}) as any),

                ViewOnly(StringCol("Location One", "locationOne", 150)),
                ViewOnly(StringCol("To/From", "locationTransition", 50)),
                ViewOnly(StringCol("Location Two", "locationTwo", 150)),

                ViewOnly(NominalWidth<InventoryRequest>("Nominal Width", sys, "nominalWidth", "inactiveDoorWidth")),
                ViewOnly(DistanceCol("Nom. Height", sys, "nominalHeight", 150)),
                ViewOnly(StringCol("Hand", "handing", 60)),

                ViewOnly(StringCol("Name", "name", 150)),
                ViewOnly(StringCol("Manufacturer", "manufacturerName", 120)),
                ViewOnly(StringCol("Code", "productCode", 150)),
                ViewOnly(StringCol("Finish", "finish", 150)),
                ViewOnly(NumberCol("Qty", "qty", 50)),
                ViewOnly(DimensionCol("Length", "dimLength", 50)),
                ViewOnly(DimensionCol("Width", "dimWidth", 50)),
                ViewOnly(DimensionCol("Height", "dimHeight", 50)),
                ViewOnly(StringCol("HW #", "hardwareGroup", 100)),
                ViewOnly(BoolCol("Keyed", "keyed"))
            ]
        case "frame-anchor":
            return [
                CheckboxSelectorCol(),
                ViewOnly(StringCol("Floor", "floor", 80)),
                PhaseViewCol<InventoryRequest>("phase", 80),
                ViewOnly(Checkbox2Col("Ext", "exterior", {disabled: (o) => true}) as any),
                AdjustCol(ViewOnly(StringCol("Opening", "openingName", 80)), {
                    fixed: true
                }),
                ViewOnly(StringCol("Location", "locationOne", 80)),
                ViewOnly(StringCol("To/From", "locationTransition", 40)),
                ViewOnly(StringCol("Location 2", "locationTwo", 80)),
                ViewOnly(StringCol("Name", "productName", 150)),
                ViewOnly(StringCol("Manufacturer", "manufacturerName", 120)),
                ViewOnly(StringCol("Code", "productCode", 150)),
                ViewOnly(NumberCol("Qty", "qty", 50)),
            ]
        case "glazing":
            return [
                CheckboxSelectorCol(),
                ViewOnly(StringCol("Opening", "openingName", 100)),
                ViewOnly(StringCol("Product", "name", 150)),
                ViewOnly(StringCol("Type", "glass.type", 90)),
                ViewOnly(StringCol("Thickness", "glass.thickness", 90)),
                ViewOnly(StringCol("Bead", "glass.bead", 90)),
                ViewOnly(StringCol("Width", "glass.width", 90)),
                ViewOnly(StringCol("Height", "glass.height", 90)),
                ViewOnly(NumberCol("Qty", "qty", 60)),
            ]
        default:
            return null;
    }
}
